import { Component, inject, ChangeDetectionStrategy, computed, signal, DestroyRef } from "@angular/core";
import { NOTIFICATION_STORE } from "../../stores";
import { tap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { ValueChangedEvent as TextBoxValueChangedEvent } from "devextreme/ui/text_box_types";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css']
})
export class NotificationsComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #notificationStore = inject(NOTIFICATION_STORE);

  readonly pageSize = signal<number>(10);
  readonly pageIndex = signal<number>(1);
  readonly pageSizeOptions = [5, 10, 20, 50, 100];

  // Track selected notification IDs
  readonly selectedIds = signal<Set<string>>(new Set());

  // Separate signal for select all checkbox to prevent recursive triggers
  readonly selectAllChecked = computed<boolean>(() => {
    const allIds = this.notifications().map(n => n.id);
    const selected = this.selectedIds();
    return allIds.length > 0 && allIds.every(n => selected.has(n));
  });

  // Api call in progress
  readonly isUpdating = signal<boolean>(false); 

  readonly notifications = computed(() => {
    const start = (this.pageIndex() - 1) * this.pageSize();
    const end = start + this.pageSize();
    return this.filteredNotifications().slice(start, end);
  });

  readonly filteredNotifications = computed(() => {
    const term = this.searchTerm().toLowerCase();
    return this.#notificationStore.notifications().filter(n => 
      n.taskName.toLowerCase().includes(term) || 
      n.body.toLowerCase().includes(term) ||      
      n.sender.toLowerCase().includes(term)
    );
  });

  readonly totalNotifications = computed(() => this.filteredNotifications().length);  

  readonly isLoading = computed(() => this.#notificationStore.status() === 'loading');
  readonly initialising = computed(() => this.#notificationStore.status() === 'initialising');

  readonly searchTerm = signal<string>('');

  onPageSizeChange(event: number) {
    this.pageSize.set(event);
  }

  onPageIndexChange(event: number) {
    console.log('Page index changed:', event);
    this.pageIndex.set(event);
  }

  onItemSelectionChange(notificationId: string, event: any) {
    const newValue = event.value;
    const currentSelected = new Set(this.selectedIds());

    if (newValue) {
      currentSelected.add(notificationId);
    } else {
      currentSelected.delete(notificationId);
    }

    this.selectedIds.set(currentSelected);
  }

  isItemSelected(notificationId: string): boolean {
    return this.selectedIds().has(notificationId);
  }

  hasSelectedItems(): boolean {
    return this.selectedIds().size > 0;
  }

  selectedItems(): string[] {
    return Array.from(this.selectedIds());
  }

  markAsRead(ids: string[]) {
    this.isUpdating.set(true);

    this.#notificationStore.markAsRead(ids).pipe(
      tap(() => {
        this.isUpdating.set(false);

        const currentSelected = new Set(this.selectedIds());
        ids.forEach(id => currentSelected.delete(id));
        this.selectedIds.set(currentSelected);
      }),
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe();
  }

  openLink(taskId: string) {
    this.#router.navigate(['/tasks'], { queryParams: { taskId } });
  }

  onSelectAllChange() {
    const action = this.selectAllChecked() ? 'deselect' : 'select';
    const allIds = this.notifications().map(n => n.id);

    switch (action) {
      case 'select':
        allIds.forEach(id => this.onItemSelectionChange(id, { value: true }));
        break;
      case 'deselect':
        allIds.forEach(id => this.onItemSelectionChange(id, { value: false }));
        break;
    }
  }

  onSearchTermChange(event: TextBoxValueChangedEvent) {
    this.searchTerm.set(event.value ?? '');
    this.pageIndex.set(1);
  }
}